import { Component, OnInit, signal } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { Business, BusinessAddress } from '@models/businesses';
import { BusinessFile } from '@models/businesses/businesses';
import {
	BusinessDocumentDefaults,
	BusinessSettings,
} from '@models/businessSettings/settings';
import { FileUploadDispatch } from '@state/file-upload/fileUpload.dispatch';
import { FileUpload } from '@state/file-upload/fileUpload.state';
import { FileUploadType } from '@state/file-upload/fileUpload.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { select, Select } from '@ngxs/store';
import { BusinessAddressesService } from '@repos/businesses/business-addresses.service';
import { BusinessesServiceRepo } from '@repos/businesses/businesses.service';
import { BusinessDispatch, BusinessState } from '@state/business';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { CardAction } from '../../../shared/atomic/organisms/contact-card/contact-card.component';
import { ICON } from '@core/util/icon';
import { LetterheadEditorDialogComponent } from '../documents/templates/letterhead-editor-dialog';
import { AddressDialogComponent } from './address-dialog/address-dialog.component';
import { UserProfileState } from '@state/user-profile';
import { BusinessSettingsVisibleTemplates } from '@models/documents';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { BusinessUsersDispatch } from '@state/business-users/business-users.dispatch';
import { Logger } from '@core/logger';
import {
	createId,
	getFileExtension,
	FILE_IMAGE_TYPES,
	createBusinessLogoFile,
} from '@core/util/file';
import { LogoDrawerComponent } from '@components/logo-drawer/logo-drawer.component';
import { BUTTON_PRESETS } from 'src/app/ui/button/button.component';
import { UploadService } from 'src/app/shared/services/file/upload.service';
import { TestHooks } from './business-details.hooks';

@UntilDestroy()
@Component({
	selector: 'app-business-details',
	templateUrl: './business-details.component.html',
	standalone: false,
})
export class BusinessDetailsComponent implements OnInit {
	testHooks = TestHooks;
	actions: { [key: string]: CardAction[] };
	addresses: BusinessAddress[];
	business: Business;
	@SelectSnapshot(UserProfileState.getCurrentBusinessId) businessId;
	@Select(BusinessState.getHstRegistrationNumber)
	hstRegistrationNum$: Observable<string>;
	@Select(BusinessState.getLogoFile)
	logoFile$: Observable<BusinessFile>;
	@SelectSnapshot(BusinessState.getDefaultSettings)
	businessSettings: BusinessSettings;

	@Select(BusinessState.getVisibleTemplates) visibleTemplates$: Observable<
		BusinessSettingsVisibleTemplates[]
	>;
	@SelectSnapshot(BusinessState.getVisibleTemplates)
	visibleTemplates: BusinessSettingsVisibleTemplates;

	public allBusinessUsers_ = select(BusinessUsersState.getUsers);

	public VisibleTemplates = BusinessSettingsVisibleTemplates;

	public hstRegistrationNo;
	public logoFile: BusinessFile;
	ICON = ICON;
	@ViewSelectSnapshot(BusinessState.getDefaultSettings)
	settings: BusinessSettings;

	public docId_ = signal('');
	private currentLogoPath_ = signal<string | null>(null);
	public readonly FILE_IMAGE_TYPES = FILE_IMAGE_TYPES;

	constructor(
		private businessDispatch: BusinessDispatch,
		private businessesServiceRepo: BusinessesServiceRepo,
		private businessAddressesService: BusinessAddressesService,
		private dialog: MatDialog,
		private snackbarService: SnackbarService,
		private drawerService: DrawerService,
		private businessUsersDispatch: BusinessUsersDispatch,
		private fileUploadDispatch: FileUploadDispatch,
		private uploadService: UploadService,
	) {}

	ngOnInit(): void {
		this.businessesServiceRepo
			.Get(this.businessId)
			.pipe(
				untilDestroyed(this),
				map((business) => business as unknown as Business),
			)
			.subscribe((business) => {
				this.business = business;
			});
		this.businessAddressesService
			.All()
			.pipe(
				untilDestroyed(this),
				map((addresses) =>
					addresses.map((address) => address as unknown as BusinessAddress),
				),
			)
			.subscribe((addresses) => {
				this.addresses = addresses;
				if (this.addresses.length === 1) {
					this.addresses[0].primary = true;
				}
				const CardActions: { [key: string]: CardAction[] } = {};
				this.addresses.forEach((address) => {
					if (
						[
							this.VisibleTemplates.HTML,
							this.VisibleTemplates.HTML_WORD,
						].includes(this.visibleTemplates)
					)
						CardActions[address.id] = [
							{
								click: () => {
									this.editLetterhead(address.id);
								},
								icon: ICON.EDIT,
								label: 'Letterhead',
							},
							{
								click: () => {
									this.editFooter(address.id);
								},
								icon: ICON.EDIT,
								label: 'Footer',
							},
						];
				});
				this.actions = CardActions;
			});
		this.hstRegistrationNum$.pipe(take(1)).subscribe((registrationNumber) => {
			this.hstRegistrationNo = registrationNumber;
		});
		this.logoFile$.pipe(untilDestroyed(this)).subscribe((logoFile) => {
			this.logoFile = logoFile;
		});
	}

	editFooter(officeId = ''): void {
		this.dialog.open(LetterheadEditorDialogComponent, {
			data: {
				mode: 'footer',
				officeId,
				officeName: this.addresses.find((address) => address.id === officeId)
					?.officeName,
			},
			height: '90vh',
			maxHeight: '1050px',
			maxWidth: '900px',
			width: '90%',
		});
	}

	editLetterhead(officeId = ''): void {
		this.dialog.open(LetterheadEditorDialogComponent, {
			data: {
				mode: 'letterhead',
				officeId,
				officeName: this.addresses.find((address) => address.id === officeId)
					?.officeName,
			},
			height: '90vh',
			maxHeight: '1000px',
			maxWidth: '900px',
			width: '90%',
		});
	}

	addAddress(): void {
		this.openAddressDrawer();
	}

	addressTrackBy(_, address: BusinessAddress): string {
		return address.id;
	}

	resetBusinessUsersAssociatedOfficeId(deletedOfficeId: string): void {
		const allBusinessUsers = this.allBusinessUsers_();
		const usersAssociatedWithDeletedOffice = allBusinessUsers.filter(
			(x) => x.associatedOfficeId === deletedOfficeId,
		);

		usersAssociatedWithDeletedOffice.forEach((user) => {
			this.businessUsersDispatch.SetAssociatedOfficeId(null, user);
		});
	}

	async deleteAddress(id: string) {
		try {
			await this.businessAddressesService.Delete(id);

			// Find users that are associated with the deleted office. If matches are found, then reset their associated office ids to null
			this.resetBusinessUsersAssociatedOfficeId(id);
		} catch (err) {
			Logger.error(
				`Failed to delete business address (office) id ${id}: ${err}`,
			);
		}
	}

	editAddress(id: string): void {
		this.openAddressDrawer(id);
	}

	setDocumentWitness($event: boolean): void {
		this.businessDispatch.UpdateDefaultSettings({
			documents: { singleWitnessSignature: $event } as BusinessDocumentDefaults,
		});
	}

	private openAddressDrawer(addressId = ''): void {
		const targetAddress = this.addresses?.find(
			(address) => address.id === addressId,
		);

		this.drawerService.openDrawer(AddressDialogComponent, {
			size: 'md',
			title: 'Office Settings',
			noStyle: true,
			data: {
				address: targetAddress,
			},
		});
	}

	submitHstRegistrationNumber(form: NgForm) {
		if (form.valid) {
			this.businessDispatch.SetHstRegistrationNumber(this.hstRegistrationNo);
			this.snackbarService.openSnackBar({
				text: 'HST registration number updated',
				type: 'success',
			});
			form.form.markAsPristine();
		}
	}

	viewLogo(): void {
		if (!this.logoFile) return;

		this.drawerService.openDrawer(LogoDrawerComponent, {
			title: 'Firm Logo',
			size: 'sm',
			titleAction: {
				icon: 'trash',
				text: 'Delete',
				buttonPreset: BUTTON_PRESETS.DANGER,
				action: () => {
					this.resetLogo();
					this.drawerService.closeDrawer();
				},
			},
			data: {
				logoFile: this.logoFile,
			},
		});
	}

	fileSelected(file: File): void {
		if (!file) return;

		this.docId_.set(createId());

		const extension = getFileExtension(file.name);
		let filePath = `${this.businessId}/logo/${this.docId_()}`;
		if (extension) {
			filePath = `${filePath}.${extension}`;
		}

		this.currentLogoPath_.set(filePath);

		this.fileUploadDispatch.UploadFile({
			uploadType: FileUploadType.BUSINESS,
			objectId: this.businessId,
			docId: this.docId_(),
			name: `business_logo`,
			path: filePath,
			file,
		});
	}

	onSuccess($event: FileUpload): void {
		if ($event && $event.publicUrl) {
			const logoFile = createBusinessLogoFile(
				$event.publicUrl,
				$event.name,
				this.businessId,
			);

			this.businessDispatch.SetLogoFile(logoFile);

			this.snackbarService.openSnackBar({
				text: 'Firm logo updated',
				type: 'success',
			});
		}
	}

	async resetLogo(): Promise<void> {
		try {
			const pathToDelete = this.currentLogoPath_();

			if (pathToDelete) {
				await this.uploadService.delete(pathToDelete);
			}

			this.currentLogoPath_.set(null);

			this.docId_.set(createId());

			this.businessDispatch.ClearLogoFile();

			this.snackbarService.openSnackBar({
				text: 'Firm logo deleted',
				type: 'success',
			});
		} catch (error) {
			Logger.error(`Failed to delete logo file: ${error}`);
			this.snackbarService.openSnackBar({
				text: 'Failed to delete logo. Please try again.',
				type: 'error',
			});
		}
	}
}
